import React, { FC } from "react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";

import { Typography } from "ui-library/atoms";
import { ContentBox, GridItem, GridLayout } from "ui-library/layouts";

import i18n from "core/i18n";
import pageContext from "core/pageContext";
import { Field } from "core/hookForm";
import { localFullDate } from "core/formatters";
import { VehicleCancellationReason } from "types";

import { EDITABLE_DATE_REASONS_CZ, EDITABLE_DATE_REASONS_SK, SECTION_MARGIN } from "../../constants";
import { VehicleCancellationFormValues } from "../../types";
import { getCancellationDateTitle } from "../../util";

type ChangeDateFieldDefinition = {
    minDate?: Dayjs;
    maxDate?: Dayjs;
};

const getChangeDateFieldDefinition = (reason: VehicleCancellationReason): ChangeDateFieldDefinition => {
    switch (reason) {
        case VehicleCancellationReason.BY_AGREEMENT:
            return {
                minDate: dayjs().add(5, "days"),
                maxDate: undefined,
            };
        default:
            return {
                minDate: undefined,
                maxDate: dayjs(),
            };
    }
};

export const ChangeDateSection: FC = () => {
    const { t } = i18n.useTranslation();
    const { watch } = useFormContext<VehicleCancellationFormValues>();

    const [selectedCancellationReason, cancellationDate] = watch(["cancellationReason", "cancellationDate"]);
    const title = getCancellationDateTitle(t, selectedCancellationReason);

    const isSlovakSite = useSelector(pageContext.getIsSlovakSite);
    const reasonsWithEditableDate = isSlovakSite ? EDITABLE_DATE_REASONS_SK : EDITABLE_DATE_REASONS_CZ;

    if (reasonsWithEditableDate.includes(selectedCancellationReason)) {
        const { minDate, maxDate } = getChangeDateFieldDefinition(selectedCancellationReason);

        return (
            <ContentBox fullWidth mb={SECTION_MARGIN}>
                <GridLayout spacing={2}>
                    <GridItem xs={12} md={6}>
                        <Field<VehicleCancellationFormValues>
                            name={"cancellationDate"}
                            input="date"
                            label={title}
                            minDate={minDate}
                            maxDate={maxDate}
                            defaultCalendarMonth={minDate}
                        />
                    </GridItem>
                </GridLayout>
            </ContentBox>
        );
    }

    return (
        <>
            <ContentBox fullWidth mb={SECTION_MARGIN}>
                <Typography variant="h4" markDown={title} />
                <Typography markDown={localFullDate(cancellationDate)} />
            </ContentBox>
        </>
    );
};
